/**
 * ArtifactHeader Component
 *
 * Cabeçalho do painel de artefatos com:
 * - Título
 * - Timestamp de atualização
 * - Botões de ação (download, copy, fullscreen, close)
 */

import React, { memo } from 'react';
import { DownloadButton } from './DownloadButton';
import { CopyButton } from './CopyButton';
import { CloseButton } from './CloseButton';
import { FullscreenButton } from './FullscreenButton';
import type { Artifact } from '../utils';

// =============================================================================
// TYPES
// =============================================================================

interface ArtifactHeaderProps {
  artifact: Artifact;
  onClose?: () => void;
  onFullscreen?: () => void;
  isFullscreen?: boolean;
  updatedAt?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);

  if (diffSecs < 60) {
    return 'atualizado agora';
  } else if (diffSecs < 3600) {
    const mins = Math.floor(diffSecs / 60);
    return `atualizado há ${mins}min`;
  } else {
    return `atualizado ${date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }
};

// =============================================================================
// ICONS
// =============================================================================

const UpdateIcon: React.FC = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
      fill="currentColor"
    />
  </svg>
);

// =============================================================================
// COMPONENT
// =============================================================================

export const ArtifactHeader: React.FC<ArtifactHeaderProps> = memo(
  ({ artifact, onClose, onFullscreen, isFullscreen = false, updatedAt }) => {
    return (
      <div className="flex items-center justify-between h-16 px-4 border-b border-white/10 bg-[#1a1a1a]">
        {/* Left side - Title */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Title */}
          <h3 className="text-sm font-medium text-white truncate max-w-[200px]" title={artifact.title}>
            {artifact.title}
          </h3>

          {/* Updated timestamp */}
          {updatedAt && (
            <div className="flex items-center gap-1 text-xs text-green-400">
              <UpdateIcon />
              <span>{formatTimestamp(updatedAt)}</span>
            </div>
          )}
        </div>

        {/* Right side - Action buttons */}
        <div className="flex items-center gap-1">
          <DownloadButton artifact={artifact} size="md" />
          <CopyButton content={artifact.content} size="md" />
          {onFullscreen && (
            <FullscreenButton isFullscreen={isFullscreen} onToggle={onFullscreen} size="md" />
          )}
          {onClose && <CloseButton onClose={onClose} size="md" />}
        </div>
      </div>
    );
  }
);

ArtifactHeader.displayName = 'ArtifactHeader';

export default ArtifactHeader;
